import { useState } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { Access } from '@prisma/client';
import { Checkbox, Input, InputProps, Popover, Space } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { LdapRoo } from '@app/integ-ldap-interface';
import { Tables, Theme } from '@app/web-components';

import { useFindIemGroup } from '../../../../bundles';
import { useAccessSelectTable } from '../../../atoms/use-access-select-table';

const PAGE_SIZE = 20;

export type IemSearchPanelProps = {
  disabled?: Tables.SelectTableDisabledType<SourceAccessItem | Access>;
  mode?: 'single' | 'multiple';
  value: (SourceAccessItem | Access)[];
  onChange?: (newValue: (SourceAccessItem | Access)[]) => void;

  searchText: string;
  searchInputProps?: InputProps;
};

const MAX_RESULT_COUNT = 100;

export const IemSearchPanel = ({ searchText, searchInputProps, ...props }: IemSearchPanelProps) => {
  const [exactMatch, setExactMatch] = useState(false);
  const { data, isFetching } = useFindIemGroup(searchText, exactMatch, MAX_RESULT_COUNT);
  const { getProps } = useAccessSelectTable<LdapRoo>();
  const { token } = Theme.useToken();

  const columns: ColumnsType<LdapRoo> = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }),
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space size="large">
        <Input
          style={{ width: 650 }}
          placeholder="Enter at least 2 letters to search by group name..."
          {...searchInputProps}
          suffix={
            data?.length === MAX_RESULT_COUNT ? (
              <Popover
                placement="bottomRight"
                trigger="hover"
                content={`Only the first ${MAX_RESULT_COUNT} results are displayed. Narrow your search to display all results`}
              >
                <WarningOutlined
                  style={{
                    cursor: 'pointer',
                    fontSize: 'large',
                    color: token.colorWarning,
                  }}
                />
              </Popover>
            ) : undefined
          }
        />
        <Checkbox checked={exactMatch} onChange={(e) => setExactMatch(e.target.checked)}>
          Exact Match
        </Checkbox>
      </Space>
      <Tables.SelectTable
        {...getProps()}
        columns={columns}
        dataSource={data}
        loading={isFetching}
        pagination={{
          total: data?.length,
          showTotal: (total) => (
            <div>
              <b>{total}</b>
              &nbsp; Records
            </div>
          ),
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
        }}
        scroll={{ y: 380 }}
        {...props}
      />
    </Space>
  );
};
